import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { map, Observable, of, shareReplay } from 'rxjs'
import { Corporation, Facility, FacilityStructure, Floor, Unit } from '../models/rpm/corporation.model';
import { TreeNode } from 'primeng/api';
import { Store } from '@ngrx/store';
import * as  Selectors from '../views/ccm/state/review-page.selectors';
import { UserRole } from '../models/user-roles.enum';
import { AuthService } from '../auth.service';

@Injectable({
  providedIn: 'root',
})
export class FacilityService {
  private cachedInfrastructureTree: TreeNode[] | null = null;
  private mappedTree$: Observable<TreeNode[]> | null = null;
  private cachedFacilities: Facility[] | null = null;

  _backendBaseUrl: string = environment.backEndUrl;
  constructor(private _http: HttpClient, private store: Store, private authService: AuthService) { }

  getInfrastructure(): Observable<{ corporations: Corporation[] }> {
    return this._http.get<{ corporations: Corporation[] }>(
      this._backendBaseUrl + '/fetchInfrastructure'
    );
  }

  postInfrastructure(
    facilityIds: string[]
  ): Observable<{ facilities_structure: FacilityStructure[] }> {
    return this._http.post<{ facilities_structure: FacilityStructure[] }>(
      this._backendBaseUrl + '/fetchStructure',
      facilityIds
    );
  }

  mapObjectToTreeData(
    corporations: Corporation[],
    facilityLeaf: boolean = false
  ): TreeNode[] {
    const profile = this.getProfileFacilities();
    const facilitiesToCache: Facility[] = [];

    // Exclude corporations without a valid organization 'code'
    corporations = corporations.filter(corp => corp.code && String(corp.code).trim() !== '');

    if (
      ![UserRole.TapestryAdmin, UserRole.TapestryAuditor, UserRole.TapestryQualityReviewer].includes(this.authService.userRole) &&
      profile()?.facilities?.length! > 0
    ) {
      const profileFacilityIds =
        profile()?.facilities?.map((f) => Number(f.gid)) ?? [];
      corporations = corporations
        .map((corp) => {
          const filteredFacilities = corp?.facilities?.filter((fac) =>
            fac.global_id && profileFacilityIds.includes(Number(fac.global_id))
          );
          // Collect facilities as we filter them
          if (filteredFacilities) {
            facilitiesToCache.push(...filteredFacilities);
          }
          return { ...corp, facilities: filteredFacilities };
        })
        .filter((corp) => corp.facilities && corp.facilities.length > 0);
    } else {
      // For admin/auditor roles, collect all facilities
      corporations.forEach((corp) => {
        if (corp.facilities) {
          facilitiesToCache.push(...corp.facilities);
        }
      });
    }

    corporations = corporations?.sort((a, b) =>
      a.name?.toLocaleLowerCase().localeCompare(b.name?.toLocaleLowerCase())
    );
    
    const treeNodes = corporations.map((groupedLookup) => {
      const groupNode: TreeNode = {
        label: groupedLookup.name,
        children: groupedLookup.facilities?.map((facility) => ({
          label: facility.name,
          data: {
            facilityId: facility.global_id?.toString(),
            type: 'Facility',
            facilityName: facility.name,
          },
          key: facility.global_id?.toString(),
          leaf: facilityLeaf,
        })),
        selectable: false,
      };
      return groupNode;
    });

    // Cache facilities collected during filtering
    this.cachedFacilities = facilitiesToCache.filter(facility => facility != null);

    return treeNodes;
  }

  mapFacilityChildren(structure: FacilityStructure[], node: TreeNode) {
    let newNodes: TreeNode[] = [];
    const newChildren: TreeNode[] = [];
    if (structure.length == 0) return;

    // Filter out null or invalid units
    structure[0].units?.filter(unit => unit?.unit != null).forEach((unit) => {
      const unitNode: TreeNode = {
        label: `Unit ${unit.unit}`,
        children: [],
        parent: {
          key: structure[0].facility_global_id.toString(),
        },
        data: {
          facilityId: structure[0].facility_global_id.toString(),
          facilityName: node.label,
          unit: unit.unit,
        },
      };
      // Filter out null or invalid floors
      unit.floors?.filter(floor => floor?.floor != null).forEach((floor) => {
        const floorNode: TreeNode = {
          label: `Floor ${floor.floor}`,
          children: [],
          parent: unitNode,
          data: {
            facilityId: structure[0].facility_global_id.toString(),
            facilityName: node.label,
            unit: unit.unit,
            floor: floor.floor,
          },
          leaf: true,
        };
        unitNode.children?.push(floorNode);
        newNodes.push(floorNode);
      });

      newChildren.push(unitNode);
      unitNode.expanded = false;
      newNodes.push(unitNode);
    });
    node.children = newChildren;
    node.expanded = true;
    return newNodes;
  }

  buildFacilityStructure(selectedNode: TreeNode): FacilityStructure | null {
    if (!selectedNode) return null;
    const facilityId = selectedNode.data?.facilityId;
    // Initialize FacilityStructure if not exists
    let facilityStructure: FacilityStructure = {
      facility_global_id: facilityId,
    };

    // Find or add Unit
    const unitName = selectedNode.data?.unit;

    if (unitName) {
      const unit: Unit = {
        unit: unitName,
      };

      const floorName = selectedNode.data?.floor;
      if (floorName) {
        const floor: Floor = {
          floor: floorName,
        };
        unit.floors = [];
        unit.floors?.push(floor);
      }
      facilityStructure.units = [];
      facilityStructure.units?.push(unit);
    }

    return facilityStructure;
  }

  getProfileFacilities() {
    return this.store.selectSignal(Selectors.selectProfile);
  }

  getMappedInfrastructureTree(): Observable<TreeNode[]> {
    if (this.cachedInfrastructureTree) {
      return of(this.cachedInfrastructureTree);
    }
    if (!this.mappedTree$) {
      this.mappedTree$ = this.getInfrastructure().pipe(
        map((response) => {
          const sortedCorporations = response.corporations?.sort((a, b) =>
            a.name?.localeCompare(b.name)
          );
          return this.buildInfrastructureTree(sortedCorporations);
        }),
        shareReplay(1)
      );
    }
    return this.mappedTree$;
  }

  buildInfrastructureTree(corporations: Corporation[]): TreeNode[] {
    const tree = this.mapObjectToTreeData(corporations);
    this.cachedInfrastructureTree = tree;
    return tree;
  }

  /**
   * Gets a facility by its name (case-insensitive partial match)
   * @param name - Facility name to search for
   * @returns Facility if found, undefined otherwise
   */
  getFacilityByName(name: string): Facility | undefined {
    if (!this.cachedFacilities || !name) {
      return undefined;
    }
    const searchName = name.toLowerCase().trim();
    return this.cachedFacilities.find(
      facility => facility.name?.toLowerCase().trim() === searchName
    );
  }
}
